import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { BookLogo, PublicBookShell } from '@/components/booking/PublicBookShell'
import { useBookCart } from '@/context/BookCartContext'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import { createPublicBooking, fetchBookingConfig, fetchBookingSlots, sendBookingOtp, verifyBookingOtp } from '@/lib/api/publicBooking'
import {
  BOOK_GENDER_TOGGLE_OPTIONS,
  filterCategoriesForBooking,
  readStoredBookPricingGender,
  resolveBookServicePrice,
  serviceBookableForGender,
  storeBookPricingGender,
} from '@/lib/bookingCatalog'
import { downloadBookingIcs } from '@/lib/bookingIcs'
import { formatBookAed, serviceDisplayName } from '@/lib/bookingDisplay'
import {
  buildSelectableDates,
  countAvailableSlots,
  firstAvailableSlot,
  groupSlotsByPeriod,
} from '@/lib/bookingSchedule'
import {
  defaultPhoneCountryForBranch,
  formatE164Display,
  formatLocalPhoneInput,
  GCC_COUNTRY_CODES,
  isGccPhoneComplete,
  PHONE_LOCAL_PLACEHOLDER,
  toE164Phone,
} from '@/lib/phone'
import { cn } from '@/lib/utils'

export function BookBranchCatalogPage() {
  const { slug } = useParams()
  const { locale, t } = useUiLocale()
  const { lines, totals, pricingGender, setPricingGender, addService, updateQty, qtyFor, clearCart } = useBookCart()
  const [config, setConfig] = useState(null)
  const [error, setError] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [filterCategoryId, setFilterCategoryId] = useState('__all__')
  const [activeCategoryId, setActiveCategoryId] = useState('')
  const [cartReviewOpen, setCartReviewOpen] = useState(false)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const sectionRefs = useRef(/** @type {Record<string, HTMLElement | null>} */ ({}))
  const pillRefs = useRef(/** @type {Record<string, HTMLButtonElement | null>} */ ({}))
  const categoryBarRef = useRef(/** @type {HTMLDivElement | null} */ (null))
  const scrollSpyPaused = useRef(false)

  useEffect(() => {
    if (!slug) {
      return
    }

    fetchBookingConfig(slug)
      .then(setConfig)
      .catch(() => setError(t('publicBook.catalog.unavailable')))
  }, [slug, t])

  useEffect(() => {
    if (!slug || !config?.pricing) {
      return
    }

    const storedGender = readStoredBookPricingGender(null, slug)
    const defaultGender = 'ladies'
    const nextGender = config.pricing.supports_gender_toggle ? storedGender : defaultGender
    setPricingGender(nextGender)
  }, [slug, config?.pricing?.supports_gender_toggle, config?.pricing?.default_gender, setPricingGender])

  useEffect(() => {
    if (!slug || !config?.pricing?.supports_gender_toggle) {
      return
    }

    storeBookPricingGender(slug, pricingGender)
  }, [slug, pricingGender, config?.pricing?.supports_gender_toggle])

  const categories = config?.catalog?.categories ?? []

  const genderCategories = useMemo(() => {
    if (!config?.pricing?.supports_gender_toggle) {
      return categories
    }

    return filterCategoriesForBooking(categories, pricingGender)
      .map((category) => ({
        ...category,
        services: category.services.filter((service) => serviceBookableForGender(service, pricingGender)),
      }))
      .filter((category) => category.services.length > 0)
  }, [categories, config?.pricing?.supports_gender_toggle, pricingGender])

  useEffect(() => {
    if (genderCategories.length > 0 && !activeCategoryId) {
      setActiveCategoryId(String(genderCategories[0].id))
    }
  }, [genderCategories, activeCategoryId])

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase()

    return genderCategories
      .filter((category) => filterCategoryId === '__all__' || String(category.id) === filterCategoryId)
      .map((category) => ({
        ...category,
        services: category.services.filter((service) => {
          if (!query) {
            return true
          }

          return (
            service.name.toLowerCase().includes(query)
            || (service.name_ar ?? '').toLowerCase().includes(query)
            || (service.description ?? '').toLowerCase().includes(query)
          )
        }),
      }))
      .filter((category) => category.services.length > 0)
  }, [genderCategories, search, filterCategoryId])

  useEffect(() => {
    const sections = filteredCategories
      .map((category) => sectionRefs.current[String(category.id)])
      .filter(Boolean)

    if (sections.length === 0) {
      return undefined
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollSpyPaused.current) {
          return
        }

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

        const topMatch = visible[0]
        if (!topMatch) {
          return
        }

        const categoryId = topMatch.target.getAttribute('data-category-id')
        if (categoryId) {
          setActiveCategoryId(categoryId)
        }
      },
      {
        root: null,
        rootMargin: '-42% 0px -48% 0px',
        threshold: [0, 0.15, 0.35, 0.55, 0.75],
      },
    )

    sections.forEach((section) => observer.observe(section))

    return () => observer.disconnect()
  }, [filteredCategories])

  useEffect(() => {
    const pill = pillRefs.current[activeCategoryId]
    if (!pill || !categoryBarRef.current) {
      return
    }

    pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }, [activeCategoryId])

  function scrollToCategory(categoryId) {
    const id = String(categoryId)
    setFilterCategoryId('__all__')
    setActiveCategoryId(id)

    scrollSpyPaused.current = true
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    window.setTimeout(() => {
      scrollSpyPaused.current = false
    }, 700)
  }

  if (error) {
    return (
      <PublicBookShell outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
        <div className="flex min-h-[100dvh] items-center justify-center px-6 text-center text-sm text-red-600">
          {error}
        </div>
      </PublicBookShell>
    )
  }

  if (!config) {
    return (
      <PublicBookShell outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
        <div className="flex min-h-[100dvh] items-center justify-center px-6 pt-10 text-center text-sm text-[#8B7E6E]">
          {t('common.loading')}
        </div>
      </PublicBookShell>
    )
  }

  const { branding, branch, policies, pricing } = config
  const homeUrl = `/book/${slug}`
  const showGenderToggle = pricing?.supports_gender_toggle === true

  return (
    <PublicBookShell branding={branding} outerClassName="bg-[#E4D7BF]" innerClassName="bg-[#F0E3CC]">
      <div className="relative flex min-h-[100dvh] flex-col">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(255,255,255,.58),transparent_42%),radial-gradient(circle_at_90%_5%,rgba(255,255,255,.32),transparent_40%),linear-gradient(180deg,rgba(139,88,46,.08),rgba(139,88,46,.03))]" />

        <header className="sticky top-0 z-30 bg-transparent pt-10">
          <div className="flex items-center justify-between border-b border-white/60 bg-white/70 px-4 pb-3 pt-3 backdrop-blur-md">
            <Link
              to={homeUrl}
              aria-label={t('publicBook.aria.goBack')}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#ECE3D0] bg-white/95 text-[#1F1611] shadow-sm transition-all hover:border-[#E5734A] active:scale-95"
            >
              <span className="book-rtl-flip">←</span>
            </Link>

            <div
              className="flex h-12 min-w-[88px] items-center justify-center overflow-hidden rounded-2xl border bg-white px-3 shadow-sm"
              style={{ borderColor: 'var(--book-secondary)' }}
            >
              <BookLogo
                url={branding.logo_url}
                name={branch.name}
                className="!text-[13px] !tracking-[0.16em] !text-[#1F1611]"
              />
            </div>

            <button
              type="button"
              aria-label={t('publicBook.aria.openMenu')}
              onClick={() => setMenuOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#ECE3D0] bg-white/95 text-[#1F1611] shadow-sm transition-all hover:border-[#E5734A] active:scale-95"
            >
              ☰
            </button>
          </div>

          <div className="flex items-center gap-2 pb-3 pt-1">
            <div className="flex shrink-0 items-center gap-2 ps-4">
              {showGenderToggle ? (
                <div
                  className="flex rounded-2xl border border-[#ECE3D0] bg-white/95 p-0.5 shadow-sm"
                  role="group"
                  aria-label={t('pos.genderPricing')}
                >
                  {BOOK_GENDER_TOGGLE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      title={t(option.titleKey)}
                      onClick={() => setPricingGender(option.value)}
                      className={cn(
                        'h-11 min-w-11 rounded-[14px] px-3 text-xs font-extrabold uppercase tracking-[0.12em] transition-all',
                        pricingGender === option.value
                          ? 'text-white shadow-sm'
                          : 'text-[#8B7E6E] hover:text-[#1F1611]',
                      )}
                      style={
                        pricingGender === option.value
                          ? { backgroundColor: 'var(--book-secondary)' }
                          : undefined
                      }
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
              <button
                type="button"
                aria-label={t('publicBook.aria.search')}
                onClick={() => setSearchOpen((value) => !value)}
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#ECE3D0] bg-white/95 text-[#1F1611] shadow-sm transition-all hover:border-[#E5734A] active:scale-95"
              >
                ⌕
              </button>
              <button
                type="button"
                aria-label={t('publicBook.aria.filter')}
                onClick={() => setFilterOpen((value) => !value)}
                className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#ECE3D0] bg-white/95 text-[#1F1611] shadow-sm transition-all hover:border-[#E5734A] active:scale-95"
              >
                ▾
              </button>
            </div>

            <div ref={categoryBarRef} className="no-scrollbar flex-1 overflow-x-auto snap-x snap-mandatory scroll-smooth">
              <div className="flex gap-3 py-1 ps-2 pe-4">
                {genderCategories.map((category) => {
                  const id = String(category.id)
                  const isActive = activeCategoryId === id

                  return (
                  <button
                    key={category.id}
                    ref={(node) => {
                      pillRefs.current[id] = node
                    }}
                    type="button"
                    onClick={() => scrollToCategory(category.id)}
                    className={cn(
                      'flex shrink-0 snap-center items-center gap-2.5 rounded-full border py-1.5 ps-1.5 pe-5 shadow-sm transition-all duration-300',
                      isActive
                        ? 'border-[#E5734A] bg-white ring-2 ring-[#E5734A]/25'
                        : 'border-white bg-white/90 opacity-80 hover:opacity-100',
                    )}
                  >
                    <span className={cn('h-11 w-11 shrink-0 overflow-hidden rounded-full ring-2', isActive ? 'ring-[#E5734A]/40' : 'ring-white')}>
                      <img src={category.image_url} alt="" className="h-full w-full object-cover" />
                    </span>
                    <span className="whitespace-nowrap text-[13px] font-extrabold uppercase tracking-[0.16em] text-[#1F1611]">
                      {category.label}
                    </span>
                  </button>
                  )
                })}
              </div>
            </div>
          </div>

          {searchOpen ? (
            <div className="px-4 pb-3">
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('publicBook.catalog.searchPlaceholder')}
                className="w-full rounded-2xl border border-[#ECE3D0] bg-white/95 px-4 py-3 text-sm text-[#1F1611] shadow-sm focus:border-[#E5734A] focus:outline-none"
              />
            </div>
          ) : null}

          {filterOpen ? (
            <div className="relative px-4 pb-2">
              <div className="rounded-2xl border border-[#ECE3D0] bg-white/95 p-3 shadow-[0_18px_42px_-24px_rgba(31,22,17,0.55)] backdrop-blur-sm">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-[#1F1611]">{t('publicBook.catalog.filterCategories')}</p>
                  <button type="button" className="text-xs text-gray-500" onClick={() => setFilterOpen(false)}>
                    {t('publicBook.catalog.done')}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterCategoryId('__all__')}
                    className="rounded-full border border-[#ECE3D0] bg-white px-3 py-2 text-start text-xs font-semibold text-[#1F1611] transition-all hover:border-[#E5734A]"
                  >
                    {t('publicBook.catalog.allCategories')}
                  </button>
                  {genderCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        setFilterCategoryId(String(category.id))
                        scrollToCategory(category.id)
                      }}
                      className="rounded-full border border-[#ECE3D0] bg-white px-3 py-2 text-start text-xs text-[#1F1611] transition-all hover:border-[#E5734A]"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </header>

        <div className="px-5 pb-2 pt-6">
          <h1 className="text-[36px] font-extrabold uppercase leading-none tracking-tight text-black">
            {t('publicBook.catalog.catalog')}
          </h1>
          <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-black">{policies.vat_note}</p>
        </div>

        <div className="space-y-10 px-4 pb-28 pt-4">
          {filteredCategories.map((category) => (
            <section
              key={category.id}
              id={`section-${category.id}`}
              data-category-id={String(category.id)}
              ref={(node) => {
                sectionRefs.current[String(category.id)] = node
              }}
              className="scroll-mt-44"
            >
              <div className="mb-4 flex items-center gap-3 px-1">
                <h2 className="text-[22px] font-extrabold uppercase tracking-[0.04em] text-black">
                  {category.name}
                </h2>
                <span className="h-px flex-1 bg-[#ECE3D0]" />
              </div>
              <ul className="space-y-3">
                {category.services.map((service) => {
                  const qty = qtyFor(service.id)
                  const displayPrice = resolveBookServicePrice(service, pricingGender)

                  return (
                    <li key={service.id}>
                      <div className="relative flex w-full items-stretch gap-4 rounded-3xl border border-white bg-white/95 p-3 shadow-[0_8px_30px_rgba(58,28,12,0.08)] backdrop-blur-sm transition-all hover:border-[#E5734A]/45 hover:shadow-[0_14px_34px_rgba(58,28,12,0.14)]">
                        <img
                          src={service.image_url}
                          alt=""
                          className="h-20 w-20 shrink-0 self-center rounded-2xl object-cover"
                        />
                        <div className="flex min-h-20 min-w-0 flex-1 flex-col">
                          <h3 className="truncate text-[14px] font-bold uppercase tracking-[0.08em] text-[#1F1611]">
                            {serviceDisplayName(service, locale)}
                          </h3>
                          <p className="mt-1 line-clamp-2 text-[13px] text-[#8B7E6E]">{service.description}</p>
                          <p
                            className="mt-auto pt-2 text-[13px] font-bold tracking-wider"
                            style={{ color: 'var(--book-secondary)' }}
                          >
                            {formatBookAed(displayPrice, t)}
                          </p>
                        </div>
                        <div className="flex min-h-20 w-[98px] shrink-0 flex-col items-end justify-end gap-1.5">
                          {qty === 0 ? (
                            <button
                              type="button"
                              onClick={() =>
                                addService({
                                  ...service,
                                  name: serviceDisplayName(service, locale),
                                  price_ladies: service.price_ladies ?? service.price,
                                  price_gents: service.price_gents,
                                })
                              }
                              className="h-10 min-w-[78px] rounded-full px-3 text-[10px] font-bold uppercase tracking-[0.11em] text-white shadow-[0_10px_22px_-12px_rgba(31,22,17,0.8)] transition-all"
                              style={{ backgroundColor: 'var(--book-secondary)' }}
                            >
                              {t('publicBook.catalog.book')}
                            </button>
                          ) : (
                            <div className="flex items-center gap-1 rounded-full bg-[#FFF4E8] px-1.5 py-1 shadow-inner">
                              <button
                                type="button"
                                onClick={() => updateQty(service.id, -1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full text-sm font-black leading-none text-white"
                                style={{ backgroundColor: 'var(--book-secondary)' }}
                              >
                                -
                              </button>
                              <span className="min-w-6 text-center text-[12px] font-extrabold text-[#1F1611]">
                                {String(qty).padStart(2, '0')}
                              </span>
                              <button
                                type="button"
                                onClick={() => updateQty(service.id, 1)}
                                className="flex h-6 w-6 items-center justify-center rounded-full text-sm font-black leading-none text-white"
                                style={{ backgroundColor: 'var(--book-secondary)' }}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>

        {totals.count > 0 ? (
          <div className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-md px-4 pb-4">
            <button
              type="button"
              onClick={() => setCartReviewOpen(true)}
              className="flex h-14 w-full items-center justify-between rounded-full px-6 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_18px_40px_-12px_rgba(31,22,17,0.75)]"
              style={{ backgroundColor: 'var(--book-secondary)' }}
            >
              <span>
                {t('publicBook.catalog.checkoutBar', {
                  count: totals.count,
                  suffix: locale === 'ar' ? (totals.count === 1 ? '' : '') : totals.count === 1 ? '' : 's',
                })}
              </span>
              <span>{t('publicBook.catalog.checkoutTotal', { total: formatBookAed(totals.total, t) })}</span>
            </button>
          </div>
        ) : null}

        {cartReviewOpen ? (
          <BookCartReviewModal
            lines={lines}
            totals={totals}
            branchName={branch.name}
            onClose={() => setCartReviewOpen(false)}
            onUpdateQty={updateQty}
            onContinue={() => {
              if (totals.count === 0) {
                return
              }
              setCartReviewOpen(false)
              setCheckoutOpen(true)
            }}
          />
        ) : null}

        {checkoutOpen ? (
          <BookCheckoutModal
            slug={slug}
            lines={lines}
            branch={branch}
            policies={policies}
            pricingGender={pricingGender}
            onClose={() => setCheckoutOpen(false)}
            onBack={() => {
              setCheckoutOpen(false)
              setCartReviewOpen(true)
            }}
            onComplete={() => {
              clearCart()
              setCheckoutOpen(false)
            }}
          />
        ) : null}

        {menuOpen ? (
          <div className="fixed inset-0 z-50">
            <button
              type="button"
              aria-label={t('publicBook.aria.closeMenuBackdrop')}
              className="absolute inset-0 bg-[#1F1611]/60 backdrop-blur-[2px]"
              onClick={() => setMenuOpen(false)}
            />
            <div className="absolute left-1/2 top-20 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-[30px] border border-white/60 bg-[#FBF6EC] p-5 shadow-[0_30px_80px_-20px_rgba(31,22,17,0.6)]">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F1EAD9]"
                >
                  ×
                </button>
              </div>
              <ul className="mt-2 divide-y divide-[#ECE3D0]">
                <li>
                  <Link to={homeUrl} onClick={() => setMenuOpen(false)} className="flex py-4 text-base font-semibold">
                    {t('publicBook.catalog.home')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        ) : null}
      </div>
    </PublicBookShell>
  )
}

function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) {
      return undefined
    }

    const { body, documentElement } = document
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflowX = documentElement.style.overflowX

    body.style.overflow = 'hidden'
    documentElement.style.overflowX = 'hidden'

    return () => {
      body.style.overflow = previousBodyOverflow
      documentElement.style.overflowX = previousHtmlOverflowX
    }
  }, [active])
}

/**
 * @param {{ lines: import('@/context/BookCartContext').CartLine[], totals: { subtotal: string, vat: string, total: string, count: number }, branchName: string, onClose: () => void, onUpdateQty: (serviceId: number, delta: number) => void, onContinue: () => void }} props
 */
function BookCartReviewModal({ lines, totals, branchName, onClose, onUpdateQty, onContinue }) {
  useBodyScrollLock(true)
  const { locale, t } = useUiLocale()

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <button type="button" aria-label={t('publicBook.aria.closeCart')} className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md overflow-hidden rounded-t-3xl bg-[#FBF6EC] shadow-[0_-12px_40px_rgba(31,22,17,0.18)]">
        <div className="max-h-[86dvh] overflow-y-auto overflow-x-hidden overscroll-contain p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#1F1611]">{t('publicBook.cart.title')}</h3>
            <p className="text-xs text-[#8B7E6E]">
              {totals.count > 0
                ? t('publicBook.cart.editHint')
                : t('publicBook.cart.reviewHint', { branch: branchName })}
            </p>
          </div>
          <button type="button" className="text-sm text-gray-500" onClick={onClose}>
            {t('publicBook.cart.close')}
          </button>
        </div>

        {lines.length === 0 ? (
          <div className="rounded-2xl border border-[#ECE3D0] bg-white/80 p-5 text-center text-sm text-[#8B7E6E]">
            {t('publicBook.cart.empty')}
          </div>
        ) : (
          <ul className="space-y-3">
            {lines.map((line) => (
              <li
                key={line.serviceId}
                className="rounded-2xl border border-[#ECE3D0] bg-white/95 p-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-bold text-sm text-[#1F1611]">{line.name}</p>
                    <p className="mt-2 text-xs font-bold" style={{ color: 'var(--book-secondary)' }}>
                      {t('publicBook.cart.each', { price: formatBookAed(line.price, t) })}
                    </p>
                  </div>
                  <div className="shrink-0 text-end">
                    <p className="font-bold text-sm text-[#1F1611]">
                      {formatBookAed(Number(line.price) * line.qty, t)}
                    </p>
                    <button
                      type="button"
                      onClick={() => onUpdateQty(line.serviceId, -line.qty)}
                      className="mt-2 text-[11px] font-bold uppercase tracking-[0.12em] text-red-600"
                    >
                      {t('publicBook.cart.remove')}
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-[#8B7E6E]">{t('publicBook.cart.quantity')}</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onUpdateQty(line.serviceId, -1)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ECE3D0] bg-[#FBF6EC] text-sm font-bold text-[#1F1611]"
                    >
                      −
                    </button>
                    <span className="min-w-6 text-center text-sm font-bold text-[#1F1611]">{line.qty}</span>
                    <button
                      type="button"
                      onClick={() => onUpdateQty(line.serviceId, 1)}
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ECE3D0] bg-[#FBF6EC] text-sm font-bold text-[#1F1611]"
                    >
                      +
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 space-y-2 rounded-2xl border border-[#ECE3D0] bg-white/90 p-4">
          <div className="flex justify-between text-sm text-[#8B7E6E]">
            <span>{t('publicBook.cart.totalItems')}</span>
            <span className="font-bold text-[#1F1611]">
              {t('publicBook.cart.itemCount', {
                count: totals.count,
                suffix: locale === 'ar' ? '' : totals.count === 1 ? '' : 's',
              })}
            </span>
          </div>
          <div className="flex justify-between text-sm text-[#8B7E6E]">
            <span>{t('publicBook.cart.subtotal')}</span>
            <span className="font-semibold text-[#1F1611]">{formatBookAed(totals.subtotal, t)}</span>
          </div>
          <div className="flex justify-between text-sm text-[#8B7E6E]">
            <span>{t('publicBook.cart.vat')}</span>
            <span className="font-semibold text-[#1F1611]">{formatBookAed(totals.vat, t)}</span>
          </div>
          <div className="flex justify-between border-t border-[#ECE3D0] pt-2 font-bold text-[#1F1611]">
            <span>{t('publicBook.cart.totalIncVat')}</span>
            <span>{formatBookAed(totals.total, t)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={onContinue}
          disabled={totals.count === 0}
          className="mt-4 h-11 w-full rounded-full font-semibold text-white disabled:opacity-60"
          style={{ backgroundColor: 'var(--book-secondary)' }}
        >
          {t('publicBook.cart.continue')}
        </button>
        </div>
      </div>
    </div>
  )
}

function minBookingDate(policies) {
  const leadMinutes = Number(policies?.lead_time_minutes ?? 120)
  const date = new Date()
  date.setMinutes(date.getMinutes() + leadMinutes)
  return date.toISOString().slice(0, 10)
}

function maxBookingDate(policies) {
  const days = Number(policies?.max_advance_days ?? 30)
  const date = new Date()
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

function expandServiceIds(lines) {
  return lines.flatMap((line) => Array.from({ length: line.qty }, () => line.serviceId))
}

/**
 * @param {{
 *   slug: string,
 *   lines: import('@/context/BookCartContext').CartLine[],
 *   branch: { whatsapp?: string, name: string, address?: string },
 *   policies: { manual_approve?: boolean, lead_time_minutes?: number, max_advance_days?: number, otp_required?: boolean },
 *   pricingGender: 'ladies' | 'gents',
 *   onClose: () => void,
 *   onBack: () => void,
 *   onComplete: () => void
 * }} props
 */
function BookCheckoutModal({ slug, lines, branch, policies, pricingGender, onClose, onBack, onComplete }) {
  useBodyScrollLock(true)
  const { locale, t } = useUiLocale()

  const [step, setStep] = useState('details')
  const [name, setName] = useState('')
  const [phoneCountry, setPhoneCountry] = useState(() => defaultPhoneCountryForBranch(branch?.country))
  const [phoneLocal, setPhoneLocal] = useState('')
  const [date, setDate] = useState('')
  const [slot, setSlot] = useState('')
  const [slots, setSlots] = useState([])
  const [notes, setNotes] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpToken, setOtpToken] = useState('')
  const [debugOtp, setDebugOtp] = useState('')
  const [otpSentMessage, setOtpSentMessage] = useState('')
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')
  const [manageUrl, setManageUrl] = useState('')
  const [appointment, setAppointment] = useState(null)
  const [error, setError] = useState('')

  const otpRequired = policies?.otp_required === true
  const otpChannel = policies?.otp_channel ?? 'sms'
  const otpUsesWhatsApp = otpRequired && otpChannel === 'whatsapp'
  const serviceIds = expandServiceIds(lines)
  const dateMin = minBookingDate(policies)
  const dateMax = maxBookingDate(policies)
  const phoneE164 = toE164Phone(phoneLocal, phoneCountry)
  const phoneComplete = isGccPhoneComplete(phoneLocal, phoneCountry)
  const phoneDisplay = phoneE164 ? formatE164Display(phoneE164) : `${phoneCountry} ${phoneLocal}`.trim()
  const selectableDates = useMemo(
    () => buildSelectableDates(dateMin, dateMax, 21, locale),
    [dateMin, dateMax, locale],
  )
  const slotGroups = useMemo(() => groupSlotsByPeriod(slots, locale), [slots, locale])
  const availableSlotCount = useMemo(() => countAvailableSlots(slots), [slots])

  useEffect(() => {
    if (phoneComplete && !date && dateMin) {
      setDate(dateMin)
    }
  }, [phoneComplete, date, dateMin])

  useEffect(() => {
    if (!phoneComplete || !date || serviceIds.length === 0) {
      setSlots([])
      setSlot('')
      return
    }

    setLoadingSlots(true)
    setError('')
    fetchBookingSlots(slug, { date, serviceIds })
      .then((items) => {
        setSlots(items)
        const firstAvailable = firstAvailableSlot(items)
        setSlot(firstAvailable?.starts_at ?? '')
      })
      .catch((err) => {
        const dateError = err?.response?.data?.errors?.date?.[0]
        const startsError = err?.response?.data?.errors?.starts_at?.[0]
        setError(dateError ?? startsError ?? err?.response?.data?.message ?? t('publicBook.checkout.errorLoadSlots'))
        setSlots([])
        setSlot('')
      })
      .finally(() => setLoadingSlots(false))
  }, [phoneComplete, date, slug, serviceIds.join(',')])

  async function handleDetailsSubmit(event) {
    event.preventDefault()
    if (!phoneComplete) {
      setError(t('publicBook.checkout.errorPhone'))
      return
    }

    setSubmitting(true)
    setError('')

    try {
      if (otpRequired) {
        const otpResponse = await sendBookingOtp(slug, { phone: phoneE164, website: '' })
        setOtpSentMessage(otpResponse.message ?? t('publicBook.checkout.codeSent'))
        if (otpResponse.data?.debug_code) {
          setDebugOtp(otpResponse.data.debug_code)
        }
        setStep('otp')
      } else {
        await submitBooking()
      }
    } catch (err) {
      setError(
        err?.response?.data?.errors?.phone?.[0]
          ?? err?.response?.data?.errors?.whatsapp?.[0]
          ?? err?.response?.data?.errors?.sms?.[0]
          ?? err?.response?.data?.message
          ?? (otpUsesWhatsApp
            ? t('publicBook.checkout.errorSendWhatsApp')
            : t('publicBook.checkout.errorSendCode')),
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleOtpSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')

    try {
      const verifyResponse = await verifyBookingOtp(slug, { phone: phoneE164, code: otpCode, website: '' })
      const token = verifyResponse.data?.otp_verification_token
      if (!token) {
        throw new Error('Verification failed')
      }
      setOtpToken(token)
      await submitBooking(token)
    } catch (err) {
      const codeError = err?.response?.data?.errors?.code?.[0]
      setError(codeError ?? err?.response?.data?.message ?? t('publicBook.checkout.errorVerify'))
    } finally {
      setSubmitting(false)
    }
  }

  async function submitBooking(verificationToken = otpToken) {
    setSubmitting(true)
    setError('')
    setMessage('')

    try {
      const response = await createPublicBooking(slug, {
        name,
        phone: phoneE164,
        starts_at: slot.replace('Z', '').slice(0, 19),
        service_ids: serviceIds,
        gender: pricingGender,
        notes: notes || undefined,
        otp_verification_token: verificationToken || undefined,
        website: '',
      })

      setMessage(response.message)
      setManageUrl(response.data?.manage_url ?? '')
      setAppointment(response.data?.appointment ?? null)
      setStep('done')
    } catch (err) {
      setError(err?.response?.data?.message ?? t('publicBook.checkout.errorSubmit'))
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  function handleDownloadIcs() {
    if (!appointment?.starts_at) {
      return
    }

    downloadBookingIcs({
      title: t('publicBook.checkout.icsTitle', { branch: branch.name }),
      startsAt: appointment.starts_at,
      endsAt: appointment.ends_at,
      location: branch.address ?? branch.name,
      description: manageUrl
        ? t('publicBook.checkout.icsDescription', { url: manageUrl })
        : t('publicBook.checkout.icsFallback'),
      uid: `appointment-${appointment.id}@saloon-erp`,
    })
  }

  const title =
    step === 'otp'
      ? t('publicBook.checkout.verifyPhone')
      : step === 'done'
        ? t('publicBook.checkout.confirmed')
        : t('publicBook.checkout.details')

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <button type="button" aria-label={t('publicBook.aria.closeCheckout')} className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-md overflow-hidden rounded-t-3xl bg-[#FBF6EC] shadow-[0_-12px_40px_rgba(31,22,17,0.18)]">
        <div className="max-h-[90dvh] overflow-y-auto overflow-x-hidden overscroll-contain px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
        <div className="mb-3 flex min-w-0 items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-[#1F1611]">{title}</h3>
            <p className="text-xs text-[#8B7E6E]">{branch.name}</p>
          </div>
          <button type="button" className="text-sm text-gray-500" onClick={onClose}>
            {t('publicBook.checkout.close')}
          </button>
        </div>

        {step === 'done' ? (
          <div className="space-y-3">
            <p className="rounded-2xl bg-green-50 px-4 py-3 text-sm text-green-800">{message}</p>
            {manageUrl ? (
              <div className="rounded-2xl border border-[#ECE3D0] bg-white px-4 py-3 text-sm">
                <p className="font-semibold text-[#1F1611]">{t('publicBook.checkout.manageLink')}</p>
                <a href={manageUrl} className="mt-1 block break-all text-[#1F1611] underline">
                  {manageUrl}
                </a>
              </div>
            ) : null}
            {appointment?.starts_at ? (
              <button
                type="button"
                className="h-10 w-full rounded-full border border-[#ECE3D0] bg-white text-sm font-semibold text-[#1F1611]"
                onClick={handleDownloadIcs}
              >
                {t('publicBook.checkout.addCalendar')}
              </button>
            ) : null}
            <button
              type="button"
              className="h-10 w-full rounded-full bg-[#1F1611] text-sm font-semibold text-white"
              onClick={onComplete}
            >
              {t('publicBook.checkout.done')}
            </button>
          </div>
        ) : step === 'otp' ? (
          <form className="space-y-5" onSubmit={handleOtpSubmit}>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" />
            <p className="text-sm text-[#8B7E6E]">
              {otpSentMessage || t('publicBook.checkout.otpPrompt', {
                channel: otpUsesWhatsApp ? t('publicBook.checkout.otpChannelWhatsApp') : '',
                phone: phoneDisplay,
              })}
            </p>
            {debugOtp ? (
              <p className="rounded-2xl bg-amber-50 px-4 py-3 text-xs text-amber-900">
                {t('publicBook.checkout.devCode', { code: debugOtp })}
              </p>
            ) : null}
            <input
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              value={otpCode}
              onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="w-full rounded-2xl border border-[#ECE3D0] bg-white px-4 py-3 text-center text-lg tracking-[0.35em] outline-none focus:border-[#E5734A]"
            />
            {error ? <p className="text-xs text-red-600">{error}</p> : null}
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setStep('details')}
                className="h-11 flex-1 rounded-full border border-[#ECE3D0] bg-white text-sm font-semibold text-[#1F1611]"
              >
                {t('publicBook.checkout.back')}
              </button>
              <button
                type="submit"
                disabled={submitting || otpCode.length !== 6}
                className="h-11 flex-[1.4] rounded-full text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: 'var(--book-secondary)' }}
              >
                {submitting ? t('publicBook.checkout.verifying') : t('publicBook.checkout.verifyBook')}
              </button>
            </div>
          </form>
        ) : (
          <form className="min-w-0 space-y-5" onSubmit={handleDetailsSubmit}>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" />

            <section className="min-w-0">
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B7E6E]">
                {t('publicBook.checkout.contactDetails')}
              </p>
              <div className="min-w-0 space-y-3 rounded-2xl border border-[#ECE3D0] bg-white/95 p-4">
                <div className="min-w-0">
                  <label htmlFor="book-name" className="mb-1 block text-xs font-semibold text-[#1F1611]">
                    {t('publicBook.checkout.name')}
                  </label>
                  <input
                    id="book-name"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t('publicBook.checkout.namePlaceholder')}
                    autoComplete="name"
                    className="box-border w-full min-w-0 max-w-full rounded-xl border border-[#ECE3D0] bg-white px-3 py-2.5 text-base outline-none focus:border-[#E5734A]"
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="book-phone-local" className="mb-1 block text-xs font-semibold text-[#1F1611]">
                    {t('publicBook.checkout.phone')}
                  </label>
                  <div className="flex min-w-0 gap-2">
                    <select
                      id="book-phone-country"
                      value={phoneCountry}
                      onChange={(event) => {
                        setPhoneCountry(event.target.value)
                        setPhoneLocal((current) => formatLocalPhoneInput(current, event.target.value))
                      }}
                      className="box-border w-[108px] shrink-0 rounded-xl border border-[#ECE3D0] bg-white px-2 py-2.5 text-xs outline-none focus:border-[#E5734A]"
                    >
                      {GCC_COUNTRY_CODES.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.label} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      id="book-phone-local"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      enterKeyHint="done"
                      required
                      value={phoneLocal}
                      onChange={(event) => {
                        setPhoneLocal(formatLocalPhoneInput(event.target.value, phoneCountry))
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                        }
                      }}
                      placeholder={PHONE_LOCAL_PLACEHOLDER}
                      className="box-border min-w-0 flex-1 rounded-xl border border-[#ECE3D0] bg-white px-3 py-2.5 text-base outline-none focus:border-[#E5734A]"
                    />
                  </div>
                  {!phoneComplete && phoneLocal ? (
                    <p className="mt-1 text-[11px] text-[#8B7E6E]">
                      {phoneCountry === '+971' || phoneCountry === '+966'
                        ? t('publicBook.checkout.phoneHintUae')
                        : t('publicBook.checkout.phoneHintDefault')}
                    </p>
                  ) : null}
                  {otpUsesWhatsApp && phoneComplete ? (
                    <p className="mt-1 text-[11px] text-[#8B7E6E]">
                      {t('publicBook.checkout.whatsappOtpHint')}
                    </p>
                  ) : null}
                </div>
              </div>
            </section>

            <section className={cn('min-w-0', !phoneComplete && 'pointer-events-none opacity-50')}>
              <div className="mb-2 px-1">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B7E6E]">
                  {t('publicBook.checkout.pickDay')}
                </p>
                {!phoneComplete ? (
                  <p className="mt-1 text-sm text-[#8B7E6E]">{t('publicBook.checkout.completePhoneFirst')}</p>
                ) : null}
              </div>
              <div className="min-w-0 space-y-4 rounded-2xl border border-[#ECE3D0] bg-white/95 p-4">
                {phoneComplete ? (
                  <>
                    <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                      {selectableDates.map((item) => {
                        const selected = date === item.value
                        return (
                          <button
                            key={item.value}
                            type="button"
                            onClick={() => setDate(item.value)}
                            className={cn(
                              'flex min-w-[4.75rem] shrink-0 flex-col items-center rounded-2xl border px-3 py-2.5 transition-all',
                              selected
                                ? 'border-[#E5734A] bg-[#FFF4E8] text-[#1F1611] ring-2 ring-[#E5734A]/20'
                                : 'border-[#ECE3D0] bg-[#FBF6EC] text-[#8B7E6E] hover:border-[#E5734A]/40',
                            )}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-[0.14em]">{item.weekday}</span>
                            <span className="mt-1 text-lg font-extrabold leading-none text-[#1F1611]">{item.day}</span>
                            <span className="mt-0.5 text-[10px] font-semibold uppercase">{item.month}</span>
                          </button>
                        )
                      })}
                    </div>

                    <div>
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <p className="text-xs font-semibold text-[#1F1611]">{t('publicBook.checkout.availableTimes')}</p>
                        {!loadingSlots && slots.length > 0 ? (
                          <span className="text-[11px] text-[#8B7E6E]">
                            {t('publicBook.checkout.openSlots', { count: availableSlotCount })}
                          </span>
                        ) : null}
                      </div>
                      {loadingSlots ? (
                        <div className="grid grid-cols-3 gap-2">
                          {Array.from({ length: 6 }).map((_, index) => (
                            <div
                              key={index}
                              className="h-12 animate-pulse rounded-2xl bg-[#F1EAD9]"
                            />
                          ))}
                        </div>
                      ) : slots.length === 0 ? (
                        <p className="rounded-2xl bg-[#FBF6EC] px-4 py-3 text-sm text-[#8B7E6E]">
                          {t('publicBook.checkout.noTimesDay')}
                        </p>
                      ) : availableSlotCount === 0 ? (
                        <p className="rounded-2xl bg-[#FBF6EC] px-4 py-3 text-sm text-[#8B7E6E]">
                          {t('publicBook.checkout.allBooked')}
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {slotGroups.map((group) => (
                            <div key={group.id}>
                              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8B7E6E]">
                                {group.label}
                              </p>
                              <div className="grid grid-cols-3 gap-2">
                                {group.items.map((item) => {
                                  const available = item.available !== false
                                  const selected = slot === item.starts_at
                                  return (
                                    <button
                                      key={item.starts_at}
                                      type="button"
                                      disabled={!available}
                                      onClick={() => {
                                        if (available) {
                                          setSlot(item.starts_at)
                                        }
                                      }}
                                      className={cn(
                                        'rounded-2xl border px-2 py-3 text-center text-sm font-semibold transition-all',
                                        !available
                                          && 'cursor-not-allowed border-[#ECE3D0]/70 bg-[#F5F0E6] text-[#BCAE9B] line-through opacity-70',
                                        available && selected
                                          && 'border-[#E5734A] bg-[#FFF4E8] text-[#1F1611] ring-2 ring-[#E5734A]/20',
                                        available && !selected
                                          && 'border-[#ECE3D0] bg-[#FBF6EC] text-[#1F1611] hover:border-[#E5734A]/60 active:scale-[0.98]',
                                      )}
                                    >
                                      {item.label}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            <section className="min-w-0">
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8B7E6E]">
                {t('publicBook.checkout.notes')}
              </p>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t('publicBook.checkout.notesPlaceholder')}
                rows={3}
                className="box-border w-full min-w-0 max-w-full rounded-2xl border border-[#ECE3D0] bg-white/95 px-4 py-3 text-base outline-none focus:border-[#E5734A]"
              />
            </section>

            {policies?.manual_approve ? (
              <p className="text-xs text-[#8B7E6E]">
                {t('publicBook.checkout.manualReview', { branch: branch.name })}
              </p>
            ) : null}
            {error ? <p className="text-xs text-red-600">{error}</p> : null}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onBack}
                className="h-11 flex-1 rounded-full border border-[#ECE3D0] bg-white text-sm font-semibold text-[#1F1611]"
              >
                {t('publicBook.checkout.backToCart')}
              </button>
              <button
                type="submit"
                disabled={submitting || !phoneComplete || !slot || serviceIds.length === 0}
                className="h-11 flex-[1.4] rounded-full text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: 'var(--book-secondary)' }}
              >
                {submitting
                  ? t('publicBook.checkout.pleaseWait')
                  : otpRequired
                    ? (otpUsesWhatsApp ? t('publicBook.checkout.sendWhatsAppCode') : t('publicBook.checkout.sendCode'))
                    : t('publicBook.checkout.confirmBooking')}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  )
}
